"use client";

import React, { useEffect, useState, memo, useCallback, useRef } from "react";
import { cn, formatAddress } from "utils";
import { Icon, ospThemes } from "@ospweb/sdk-ui";
import { useBalance } from "wagmi";
import { formatUnits } from "viem";
import Translations from "@translate/components/Payment/PaymentRadio/PaymentRadio";
import { I18nUtil } from "@utils/i18n";
import NextImage from "@/src/components/NextImage";

const I18n = I18nUtil.createScoped(Translations);

interface PaymentRadioProps {
  /** 可选的自定义样式类名 */
  className?: string;
  /** 网络变化回调 */
  onNetworkChange?: (network: any) => void;
  networkList?: any[];
  address: `0x${string}`;
  isConnected: boolean;
  onConnect: () => void;
  isConnecting: boolean;
  disabled?: boolean;
}

const PaymentRadioComponent: React.FC<PaymentRadioProps> = ({
  className,
  networkList,
  onNetworkChange,
  address,
  isConnected,
  onConnect,
  isConnecting,
  disabled = false,
}) => {
  const [selectedNetwork, setSelectedNetwork] = useState<any>();
  const { data: balance, refetch: refetchBalance } = useBalance({
    address: address,
    token: selectedNetwork?.address,
    chainId: Number(selectedNetwork?.chain_id as any),
    query: {
      retry: 3, // 自动重试次数
    },
  });

  // 使用 useRef 来存储最新的 onNetworkChange 回调，避免依赖变化导致重新渲染
  const onNetworkChangeRef = useRef(onNetworkChange);
  onNetworkChangeRef.current = onNetworkChange;

  // 创建稳定的网络变化处理函数
  const handleNetworkChange = useCallback(
    (network: any) => {
      if (disabled || isConnecting) {
        return;
      }
      setSelectedNetwork(network);
      refetchBalance();
      onNetworkChangeRef.current?.(network);
    },
    [refetchBalance, disabled, isConnecting],
  );

  useEffect(() => {
    if (networkList?.length === 0) {
      return;
    }
    handleNetworkChange(networkList?.[0]);
  }, [networkList, handleNetworkChange]);

  return (
    <div className={cn("flex flex-col items-start gap-3 relative", className)}>
      {/* Title */}
      <div className="text-content_primary body_medium_l relative whitespace-nowrap w-fit -mt-px">
        {I18n.t("Payment method")}
      </div>
      {/* Main Content */}
      <div className="flex flex-col gap-3 relative w-full border border-background_white_overlay10 rounded-xl">
        {/* Crypto Option */}
        <div
          className={cn(
            "flex justify-start items-center gap-4 relative w-full p-4 cursor-pointer",
          )}
        >
          <div className="flex items-center gap-2 relative flex-1">
            <div className="flex items-center justify-center w-6 h-6 relative rounded-full bg-background_brand_overlay10">
              <Icon
                name="wallet"
                size={16}
                className="text-content_brand_primary"
              />
            </div>
            <div className="body_l text-content_primary">
              {I18n.t("Crypto")}
            </div>
          </div>
        </div>

        {/* Crypto Options */}
        <div
          className={cn("flex flex-col items-start relative w-full px-4 pb-4")}
        >
          <div className="flex flex-col items-start gap-3 relative w-full">
            <div className="text-content_tertiary body_m">
              {I18n.t("Choose Token and Network")}
            </div>
            {networkList?.map((item: any) => {
              return (
                <div
                  data-openreplay-label="PaymentRadio · PaymentRadio · div"
                  key={item?.chain_id + item?.price}
                  className={cn(
                    "flex items-center self-stretch border rounded-lg gap-1 min-w-14 overflow-hidden px-5 py-3 relative w-full cursor-pointer",
                    selectedNetwork?.chain_id === item.chain_id
                      ? "bg-background_brand_overlay10 border-border_brand_transparent50"
                      : "bg-background_white_overlay5 border-transparent",
                  )}
                  onClick={() => handleNetworkChange(item)}
                >
                  <div className="flex items-center flex-1 gap-1 justify-around relative">
                    <div className="flex items-center inline-flex gap-1 relative">
                      <NextImage
                        alt={item.description}
                        className="w-4 h-4 rounded-full"
                        src={item.icon}
                      />
                      <div className="text-content_primary body_medium_m relative whitespace-nowrap w-fit -mt-px">
                        {item.description}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Payment Section */}
          {!isConnected ? (
            <button
              type="button"
              data-openreplay-label={`PaymentRadio · Button | ${I18n.t("Connect Wallet")}`}
              className={cn(
                "button sm_button primary_button button_s group w-full mt-6 button_m",
              )}
              onClick={onConnect}
              data-loading={isConnecting}
              disabled={isConnecting}
            >
              <span className="group-loading:hidden">
                {I18n.t("Connect Wallet")}
              </span>
              <Icon
                name="ic_loading2"
                size={16}
                color={ospThemes.colors.content_inverse_primary}
                className={cn("hidden animate-spin group-loading:block")}
              />
            </button>
          ) : (
            <div className="flex flex-col items-start gap-3 relative w-full mt-4">
              <div className="text-content_tertiary body_m relative whitespace-nowrap w-fit -mt-px">
                {I18n.t("Address Used:")}
              </div>

              <div className="flex items-center self-stretch border border-background_white_overlay10 rounded-lg justify-between p-3 relative w-full">
                <div className="flex items-center inline-flex gap-2 relative">
                  <div className="flex items-center justify-center bg-background_white_overlay5 rounded-full gap-2.5 w-8 h-8 relative">
                    <Icon
                      name="wallet"
                      size={16}
                      className="text-content_primary"
                    />
                  </div>

                  <div className="flex flex-col items-start inline-flex justify-center relative">
                    <div className="text-content_primary body_s relative whitespace-nowrap w-fit -mt-px">
                      {formatAddress(address, 4)}
                    </div>
                    <div className="text-content_secondary body_s relative text-right whitespace-nowrap w-fit">
                      {I18n.t("Available")}{" "}
                      {balance?.value ? (
                        <>
                          {formatUnits(
                            BigInt(balance?.value || 0),
                            selectedNetwork?.decimals,
                          )}{" "}
                          {balance?.symbol}
                        </>
                      ) : (
                        "--"
                      )}
                    </div>
                  </div>
                </div>

                <button
                  type="button"
                  data-openreplay-label="PaymentRadio · Button"
                  onClick={onConnect}
                  className={cn("button sm_button text_button")}
                  disabled={disabled}
                >
                  <span className="flex items-center gap-0.5">
                    <Icon
                      name="ic_withdraw"
                      size={16}
                      className="text-content_brand_primary"
                    />
                    <span className="text-content_brand_primary button_s relative text-center whitespace-nowrap w-fit -mt-px">
                      {I18n.t("Switch wallet")}
                    </span>
                  </span>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// 自定义比较函数，只比较重要的 props
const areEqual = (
  prevProps: PaymentRadioProps,
  nextProps: PaymentRadioProps,
) => {
  return (
    // 对于函数 props（onNetworkChange），我们通过 useRef 处理，所以不需要比较
    prevProps.className === nextProps.className &&
    // 对于 networkList，我们使用引用比较，如果父组件正确使用了 useMemo，引用应该是稳定的
    prevProps.networkList === nextProps.networkList &&
    prevProps.isConnected === nextProps.isConnected &&
    prevProps.isConnecting === nextProps.isConnecting &&
    prevProps.address === nextProps.address
  );
};

export const PaymentRadio = memo(PaymentRadioComponent, areEqual);
